// ============================================================================
// SEO CONFIGURATION - EASY TO EDIT!
// ============================================================================
// Instructions for non-technical editors:
// 1. To change page titles, descriptions, or images: Edit the PAGE_METADATA section below
// 2. To add a new page: Add to PageKey enum, then add entry to PAGE_METADATA
// 3. Images should be 1200x630px and use either an absolute URL or a /public path
// 4. Keep descriptions under 160 characters for best results
// ============================================================================

// ============================================================================
// PAGE KEYS - Add new pages here first!
// ============================================================================
// This enum defines all pages that have SEO metadata
// When adding a new page: Add it here, then add its metadata below
export enum PageKey {
    HOME = 'home',
    PRICING = 'pricing',
    MOBILE_APP = 'mobile-app',
    E_COMMERCE = 'e-commerce',
    WEBSITE = 'website',
    SHOPIFY_STORE = 'shopify-store',
    AGENCY = 'agency',
    AGENT_LP = 'agent',
    CREATIVE_STRATEGIST = 'creative-strategist',
    AD_RESEARCH = 'ad-research',
    STATIC_VIDEO_ADS = 'static-video-ads',
    MEDIA_BUYING = 'media-buying',
    AD_ANALYSIS = 'ad-analysis',
    PARTNER_PROGRAM = 'partner-program',
    D2C = 'd2c',
    CASE_STUDY = 'case-study',
    AI_ADS = 'ai-ads',
    MANUS = 'manus',
}

export const SEO_CONFIG = {
    // Site-wide settings
    siteName: 'Superscale',
    siteUrl: process.env.NEXT_PUBLIC_WEB_URL || 'https://superscale.ai',
    description: 'AI-powered marketing platform to create, launch and iterate performance ad campaigns',
    twitterHandle: '@superscale_ai',
    defaultLocale: 'en',
    author: 'Superscale',

    // Default fallback image (1200x630px)
    defaultOgImage: 'https://vfzu8wybscobwawi.public.blob.vercel-storage.com/social-graph',

    // External content sources (usually don't need to change these)
    externalSources: {
        ghost: {
            url: 'https://superscale-1.ghost.io',
            contentApiUrl: 'https://superscale-1.ghost.io/learn/ghost/api/content',
        },
    },
} as const

// ============================================================================
// PAGE METADATA - EDIT THIS TO CHANGE SEO FOR EACH PAGE
// ============================================================================
// How to use:
// - title: Shows in browser tab and Google search results (aim for 50-60 characters)
// - description: Shows under title in Google (aim for 140-160 characters)
// - keywords: Comma-separated list of relevant search terms
// - ogImage: Absolute image URL or path in /public (must be 1200x630px)
// ============================================================================

export const PAGE_METADATA: Record<
    PageKey,
    {
        title: string
        description: string
        keywords: string[]
        ogImage: string
    }
> = {
    // Homepage
    [PageKey.HOME]: {
        title: 'Superscale - AI-Powered Marketing Platform',
        description:
            'Create, launch, and iterate performance ad campaigns with AI. Generate video ads, copy, and creative assets in minutes.',
        keywords: ['AI marketing', 'performance ads', 'ad creation', 'video ads', 'marketing automation'],
        ogImage: SEO_CONFIG.defaultOgImage,
    },

    // Pricing Page
    [PageKey.PRICING]: {
        title: 'Pricing - Superscale',
        description:
            'Simple, transparent pricing for AI-powered ad creation. Choose the plan that fits your team and growth stage.',
        keywords: ['pricing', 'subscription', 'plans', 'ai ad creation pricing'],
        ogImage: SEO_CONFIG.defaultOgImage,
    },

    // Mobile App Marketing Page
    [PageKey.MOBILE_APP]: {
        title: 'Mobile App Marketing - AI-Powered User Acquisition',
        description:
            'Scale your mobile app with AI-generated ad campaigns. Create performance ads optimized for app installs and user acquisition across all platforms.',
        keywords: ['mobile app marketing', 'app user acquisition', 'app install ads', 'mobile marketing AI'],
        ogImage: SEO_CONFIG.defaultOgImage,
    },

    // E-Commerce Marketing Page
    [PageKey.E_COMMERCE]: {
        title: 'E-Commerce Marketing - AI Ad Creation for Online Stores',
        description:
            'Drive e-commerce sales with AI-generated product ads. Create high-converting campaigns for your online store in minutes.',
        keywords: ['ecommerce marketing', 'online store ads', 'product advertising', 'DTC marketing'],
        ogImage: SEO_CONFIG.defaultOgImage,
    },

    // Website Marketing Page
    [PageKey.WEBSITE]: {
        title: 'Website Marketing - AI-Powered Performance Ads',
        description:
            'Generate performance ad campaigns for your website. Drive traffic and conversions with AI-created marketing content.',
        keywords: ['website marketing', 'traffic generation', 'performance ads', 'website promotion'],
        ogImage: SEO_CONFIG.defaultOgImage,
    },

    // Shopify Store Marketing Page
    [PageKey.SHOPIFY_STORE]: {
        title: 'Shopify Marketing - AI Ads for Shopify Stores',
        description:
            'Supercharge your Shopify store with AI-powered advertising. Create product ads that convert in minutes.',
        keywords: ['shopify marketing', 'shopify ads', 'ecommerce automation'],
        ogImage: SEO_CONFIG.defaultOgImage,
    },

    // Agent Landing Page
    [PageKey.AGENT_LP]: {
        title: 'Superscale Agent - Claude Code for Marketing',
        description:
            'An autonomous AI co-worker that handles research, analysis, ideation, testing, and creative production on a schedule. Apply to the beta today.',
        keywords: [
            'AI marketing agent',
            'autonomous marketing',
            'AI co-worker',
            'marketing automation',
            'creative production AI',
            'marketing research AI',
        ],
        ogImage: SEO_CONFIG.defaultOgImage,
    },

    // Creative Strategist Page
    [PageKey.CREATIVE_STRATEGIST]: {
        title: 'Hire a Superhuman Creative Strategist from $199/mo - Superscale',
        description:
            "Superscale understands what's working for your competitors and in your ad account, identifies patterns, and builds new creatives based on that. Used by 500+ growth teams.",
        keywords: [
            'creative strategist',
            'AI creative strategist',
            'hire creative strategist',
            'ad creative analysis',
            'competitor ad analysis',
            'creative pattern recognition',
            'AI ad creative',
        ],
        ogImage: SEO_CONFIG.defaultOgImage,
    },

    [PageKey.AD_RESEARCH]: {
        title: 'Ad Research - Know which competitor ads work - Superscale',
        description:
            'Superscale monitors every ad in your niche, separates the winners from the noise, and turns them into insights you can act on today.',
        keywords: ['ad research', 'competitor ads', 'ad library', 'winning ads', 'AI ad research'],
        ogImage: SEO_CONFIG.defaultOgImage,
    },

    [PageKey.STATIC_VIDEO_ADS]: {
        title: 'Static & Video Ads - Creative that ships - Superscale',
        description:
            "Superscale learns from what's winning in your account and your market, then builds statics and videos ready to launch — new concepts every week.",
        keywords: ['static ads', 'video ads', 'AI ad generation', 'batch creative', 'performance ads'],
        ogImage: SEO_CONFIG.defaultOgImage,
    },

    [PageKey.MEDIA_BUYING]: {
        title: 'Media Buying - Your budget never sleeps - Superscale',
        description:
            'Superscale manages your campaigns 24/7 — cutting losers early, scaling winners fast, and showing you the reasoning behind every move.',
        keywords: ['media buying', 'AI media buyer', 'campaign management', 'Meta ads automation'],
        ogImage: SEO_CONFIG.defaultOgImage,
    },

    [PageKey.AD_ANALYSIS]: {
        title: 'Ad Analysis - Stop guessing - Superscale',
        description:
            "Superscale digs into your data, finds why your best ads win and your worst ones don't, and gives you takeaways in plain language.",
        keywords: ['ad analysis', 'creative analytics', 'ad account insights', 'performance analysis'],
        ogImage: SEO_CONFIG.defaultOgImage,
    },

    // Partner Program Page
    [PageKey.PARTNER_PROGRAM]: {
        title: 'Partner Program for Agencies & Freelancers',
        description:
            'Give every client more ads, better ads, faster — and earn a recurring revenue share on every account you bring. The tool comes first. The income follows.',
        keywords: [
            'partner program',
            'agency partner program',
            'affiliate program',
            'revenue share',
            'AI ads for agencies',
            'freelancer marketing tools',
        ],
        ogImage: SEO_CONFIG.defaultOgImage,
    },

    // D2C Offer Page
    [PageKey.D2C]: {
        title: 'Performance Design for D2C Brands',
        description:
            'Meta rewards whoever tests the most creative. One creative lead, a fleet of agents: statics, video, website content, and organic social for D2C brands — ten times the output, month to month.',
        keywords: [
            'D2C creative agency',
            'performance design',
            'Meta ads creative',
            'creative subscription',
            'D2C ad creative',
            'creative volume',
        ],
        ogImage: '/images/og-images/default-og-image.jpg',
    },

    // Case-study index (code-owned route; values carried over from the retired content-pages entry)
    [PageKey.CASE_STUDY]: {
        title: 'Superscale Case Studies: real results from AI ad creative',
        description:
            'Real Superscale customer results, Taxfix, SumUp, Lila, StromNow and more scaling AI ad creative across Meta and TikTok. See the case studies.',
        keywords: ['case studies', 'AI ad creative results', 'Meta ads case study', 'TikTok ads case study', 'AI UGC results'],
        ogImage: SEO_CONFIG.defaultOgImage,
    },

    // AI Ads Generator Page (paid-search LP for the "ai ads generator" ad group, also indexed)
    [PageKey.AI_ADS]: {
        // Root layout template appends "| Superscale".
        title: 'AI Ads Generator - Turn Any Product Link Into Publish-Ready Ads',
        description:
            'Turn any product link into publish-ready ads for Meta, TikTok & Google. AI builds the copy, creative, and video; no designer, no agency, no waiting.',
        keywords: [
            'AI ads generator',
            'AI ad maker',
            'winning creatives',
            'AI ads',
            'product link to ads',
            'competitor ad research',
        ],
        ogImage: SEO_CONFIG.defaultOgImage,
    },

    // Agency Page
    [PageKey.AGENCY]: {
        title: 'For Agencies - The AI Tool for Your Creative Agency',
        description:
            'One platform for client onboarding, creative research, and ad production. Scale creative output across all your clients with AI.',
        keywords: [
            'agency ad production',
            'creative agency platform',
            'AI ad creation agency',
            'scale creative production',
            'UGC agency tools',
            'agency creative workflow',
        ],
        ogImage: SEO_CONFIG.defaultOgImage,
    },

    // Manus alternative — competitor-conquest lander (paid search + organic "manus alternative")
    [PageKey.MANUS]: {
        title: 'Manus Alternative for Marketers (2026)',
        description:
            'Manus is separating from Meta: accounts deleted, task data wiped, roadmap frozen. Here is the specialized alternative for ad creative and media buying.',
        keywords: [
            'Manus alternative',
            'Manus AI alternative',
            'alternative to Manus',
            'Manus competitor',
            'AI ad agent',
            'AI media buying agent',
        ],
        ogImage: SEO_CONFIG.defaultOgImage,
    },
}

export const PAGE_ROUTES: Record<PageKey, string> = {
    [PageKey.HOME]: '/',
    [PageKey.PRICING]: '/pricing',
    [PageKey.MOBILE_APP]: '/products/mobile-app',
    [PageKey.E_COMMERCE]: '/products/e-commerce',
    [PageKey.WEBSITE]: '/products/website',
    [PageKey.SHOPIFY_STORE]: '/shopify-store',
    [PageKey.AGENCY]: '/groups/agency',
    [PageKey.AGENT_LP]: '/agent',
    [PageKey.CREATIVE_STRATEGIST]: '/creative-strategist',
    [PageKey.AD_RESEARCH]: '/ad-research',
    [PageKey.STATIC_VIDEO_ADS]: '/static-video-ads',
    [PageKey.MEDIA_BUYING]: '/media-buying',
    [PageKey.AD_ANALYSIS]: '/ad-analysis',
    [PageKey.PARTNER_PROGRAM]: '/partner-program',
    [PageKey.D2C]: '/d2c',
    [PageKey.CASE_STUDY]: '/case-study',
    [PageKey.AI_ADS]: '/ai-ads',
    [PageKey.MANUS]: '/manus',
}

// Public routes (pages that should be in sitemap and accessible to search engines)
/** Routes advertised in the sitemap. /d2c is excluded: its page metadata sets robots noindex
 * (paid-offer page), and a sitemap entry for a noindexed URL earns Search Console warnings. */
export const PUBLIC_ROUTES: string[] = Object.values(PAGE_ROUTES).filter((route) => route !== PAGE_ROUTES[PageKey.D2C])
